import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import Authorize from '../../components/AuthComponent/Authorize';
import SaveButton from '../../components/Buttons/SaveButton';
import ButtonBar from '../../components/Fields/ButtonBar';
import Form from '../../components/Fields/Form';
import SelectListField from '../../components/Fields/SelectListField';
import TextboxField from '../../components/Fields/TextboxField';
import { FormConfig } from '../../components/Fields/Types';
import useSelectListOptions from '../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import SpinnerOverlay from '../../components/Spinner/SpinnerOverlay';
import { ApplicationFunctions } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import DepartmentModel from '../../models/DepartmentModel';
import DataService from '../../services/data.service';
import IdParams from '../../types/IdParams';
import { DataResult } from '../../types/Result';
import Unauthorized from '../Authentication/Unauthorized';

const DepartmentView: React.FC = () => {
    const employees = useSelectListOptions('api/v1/selectlist/Employees', '-- Select manager');

    const [serverErrors, setServerErrors] = useState<DataResult<DepartmentModel>>({ isSuccesful: true, messages: [], data: undefined });
    const { handleResult, handleException } = useNotifications();
    const [isAuthorized, setIsAuthorized] = useState<boolean>(true);

    const [formData, setFormData] = useState<DepartmentModel>({
        id: 0,
        name: '',
        managerEmployeeId: 0,
        concurrencyId: ''
    });

    let idParams: IdParams = useParams();
    let departmentId: number | undefined;
    if (idParams.id && !Number.isNaN(parseInt(idParams.id))) {
        departmentId = parseInt(idParams.id);
    }

    useEffect(() => {
        if (departmentId !== undefined) {
            DataService.getById<DepartmentModel>('api/v1/departments/', departmentId)
                .then(response => setFormData(response))
                .catch(e => {
                    if (e.response?.data?.status === 401)
                        setIsAuthorized(false);
                    else
                        handleException(e);
                });
        }
    }, [departmentId]);

    function submit() {
        if (formData) {
            DataService
                .update<DepartmentModel>(`api/v1/departments/`, formData)
                .then(response => {
                    handleResult(response);
                    setServerErrors(response);
                    if (response.isSuccesful === true && response.data)
                        setFormData(response.data);
                })
                .catch(e => handleException(e));
        }
    }

    function onFieldChanged(name: string, value: string): void {
        formData[name] = value;
        setFormData(formData);
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'name', label: 'Name', validationRules: [{ type: 'isRequired' }, { type: 'isMaxLength', maxLength:50 }] },
            { name: 'managerEmployeeId', label: 'Manager', validationRules: [{ type: 'isRequired' }] },
        ],
        handleSubmit: submit,
        handleFieldChanged: onFieldChanged,
        showErrors: 'always',
    };

    const { getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    if (!isAuthorized)
        return (<Unauthorized />);

    if (!formData)
        return <SpinnerOverlay />

    return (
        <div className='main-content'>
            <Form title='Department' footer={(
                <Row>
                    <Col md='12'>
                        <ButtonBar offset={2}>
                            <Authorize applicationFunction={ApplicationFunctions.UpdateDepartment}>
                                <SaveButton  {...getSubmitButtonProps()} />
                            </Authorize>
                        </ButtonBar>
                    </Col>
                </Row>
            )}>
                <Container fluid>
                    <Row>
                        <Col md='12'>
                            <TextboxField {...getFieldProps('name')} />
                            <SelectListField {...getFieldProps('managerEmployeeId')} options={employees} />
                        </Col>
                    </Row>
                </Container>
            </Form>
        </div>
    );
}
export default DepartmentView;